import { Box, MenuItem, Stack, TextField } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import AsyncMultiPicker from "src/grading/components/AsyncMultiPicker/AsyncMultiPicker";
import { AssignmentRole } from "src/models/staff";
import { useListClassesQuery } from "src/store/reducers/classes/classesApiSlice";
import { useListStaffQuery } from "src/store/reducers/staff/staffApiSlice";

export const ASSIGNMENT_ROLES: readonly AssignmentRole[] = ["teacher", "aide"];

/** A class picked for one person (mode `byPerson`). */
export interface PickedClass {
  classId: string;
  className: string;
  schoolName?: string | null;
  role: AssignmentRole;
}

/** A staff member picked for one class (mode `byClass`). */
export interface PickedStaff {
  profileId: string;
  name: string;
  email?: string;
  /** Invited but not signed in yet. */
  pending?: boolean;
  role: AssignmentRole;
}

interface CommonProps {
  label: string;
  /** Platform admins' district; others are scoped by the server. */
  districtId?: string;
  /** Narrows the search to one school. */
  schoolId?: string;
  disabled?: boolean;
  helperText?: ReactNode;
  /** Show a teacher/aide select per picked item. Defaults to true. */
  showRoles?: boolean;
}

export type ClassAssignmentPickerProps = CommonProps &
  (
    | {
        mode: "byPerson";
        value: PickedClass[];
        onChange: (value: PickedClass[]) => void;
      }
    | {
        mode: "byClass";
        value: PickedStaff[];
        onChange: (value: PickedStaff[]) => void;
      }
  );

const SEARCH_LIMIT = 25;

/** Keeps the role already chosen for an item that stays picked; new items start as teacher. */
const mergeRoles = <T extends { role: AssignmentRole }>(
  next: T[],
  current: T[],
  keyOf: (item: T) => string,
) =>
  next.map((item) => {
    const kept = current.find((other) => keyOf(other) === keyOf(item));
    return kept ? { ...item, role: kept.role } : item;
  });

interface RoleListProps<T> {
  items: T[];
  keyOf: (item: T) => string;
  nameOf: (item: T) => string;
  roleLabel: (name: string) => string;
  disabled?: boolean;
  onRoleChange: (item: T, role: AssignmentRole) => void;
}

const RoleList = <T extends { role: AssignmentRole }>({
  items,
  keyOf,
  nameOf,
  roleLabel,
  disabled,
  onRoleChange,
}: RoleListProps<T>) => {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <Stack component="ul" spacing={1} sx={{ m: 0, p: 0, listStyle: "none" }}>
      {items.map((item) => (
        <Box
          component="li"
          key={keyOf(item)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box component="span" sx={{ fontWeight: 700, minWidth: 0 }}>
            {nameOf(item)}
          </Box>
          <TextField
            select
            size="small"
            disabled={disabled}
            value={item.role}
            onChange={(event) =>
              onRoleChange(item, event.target.value as AssignmentRole)
            }
            label={roleLabel(nameOf(item))}
            sx={{ minWidth: 150 }}
          >
            {ASSIGNMENT_ROLES.map((role) => (
              <MenuItem key={role} value={role}>
                {t(`staff.assignmentRole.${role}`)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      ))}
    </Stack>
  );
};

const ByPersonPicker = ({
  label,
  districtId,
  schoolId,
  disabled,
  helperText,
  showRoles = true,
  value,
  onChange,
}: CommonProps & {
  value: PickedClass[];
  onChange: (value: PickedClass[]) => void;
}) => {
  const { t } = useTranslation();
  const useSearch = (term: string) => {
    const { data, isLoading, isFetching } = useListClassesQuery({
      districtId,
      schoolId,
      search: term || undefined,
      limit: SEARCH_LIMIT,
    });
    return {
      data: data?.items.map<PickedClass>((item) => ({
        classId: item.id,
        className: item.name,
        schoolName: item.schoolName,
        role: "teacher",
      })),
      isLoading,
      isFetching,
    };
  };
  const keyOf = (item: PickedClass) => item.classId;

  return (
    <Stack spacing={1.5}>
      <AsyncMultiPicker<PickedClass>
        label={label}
        useSearch={useSearch}
        getOptionKey={keyOf}
        getOptionLabel={(item) => item.className}
        renderOptionExtra={(item) => item.schoolName ?? null}
        value={value}
        onChange={(next) => onChange(mergeRoles(next, value, keyOf))}
        disabled={disabled}
        helperText={helperText}
      />
      {showRoles ? (
        <RoleList
          items={value}
          keyOf={keyOf}
          nameOf={(item) => item.className}
          disabled={disabled}
          roleLabel={(name) => t("staff.assign.roleInClass", { name })}
          onRoleChange={(item, role) =>
            onChange(
              value.map((other) =>
                keyOf(other) === keyOf(item) ? { ...other, role } : other,
              ),
            )
          }
        />
      ) : null}
    </Stack>
  );
};

const ByClassPicker = ({
  label,
  districtId,
  schoolId,
  disabled,
  helperText,
  showRoles = true,
  value,
  onChange,
}: CommonProps & {
  value: PickedStaff[];
  onChange: (value: PickedStaff[]) => void;
}) => {
  const { t } = useTranslation();
  const notSignedIn = t("classes.notSignedInYet");
  const useSearch = (term: string) => {
    const { data, isLoading, isFetching } = useListStaffQuery({
      districtId,
      schoolId,
      roles: ["teacher", "school_admin"],
      search: term || undefined,
      limit: SEARCH_LIMIT,
    });
    return {
      data: data?.items
        .filter((person) => person.status !== "deactivated")
        .map<PickedStaff>((person) => ({
          profileId: person.id,
          name: `${person.firstName} ${person.lastName}`.trim(),
          email: person.email,
          pending: person.status === "invited",
          role: "teacher",
        })),
      isLoading,
      isFetching,
    };
  };
  const keyOf = (item: PickedStaff) => item.profileId;
  const nameOf = (item: PickedStaff) =>
    item.pending ? `${item.name} ${notSignedIn}` : item.name;

  return (
    <Stack spacing={1.5}>
      <AsyncMultiPicker<PickedStaff>
        label={label}
        useSearch={useSearch}
        getOptionKey={keyOf}
        getOptionLabel={nameOf}
        renderOptionExtra={(item) => item.email ?? null}
        value={value}
        onChange={(next) => onChange(mergeRoles(next, value, keyOf))}
        disabled={disabled}
        helperText={helperText}
      />
      {showRoles ? (
        <RoleList
          items={value}
          keyOf={keyOf}
          nameOf={nameOf}
          disabled={disabled}
          roleLabel={(name) => t("staff.assign.roleFor", { name })}
          onRoleChange={(item, role) =>
            onChange(
              value.map((other) =>
                keyOf(other) === keyOf(item) ? { ...other, role } : other,
              ),
            )
          }
        />
      ) : null}
    </Stack>
  );
};

/**
 * Assigns staff to classes as teacher or aide, searching the server. `byPerson` picks classes for
 * one person; `byClass` picks teachers and school admins for one class.
 */
export const ClassAssignmentPicker = (props: ClassAssignmentPickerProps) =>
  props.mode === "byPerson" ? (
    <ByPersonPicker {...props} />
  ) : (
    <ByClassPicker {...props} />
  );

export default ClassAssignmentPicker;
